import { useState, useEffect, useRef } from 'react';
import { TimerBanner, GlobalDenyAlert } from '@sentara-health/optimahealth_web';
import { useTranslation } from 'react-i18next';
import {
  stopCoBrowsing,
  approveVendorConsent,
  denyVendorConsent,
  setCoBrowseCallbacks,
  clearCoBrowseCallbacks,
} from './openreplay';
import CoBrowseConsentModal from './CoBrowseConsentModal';
import LongButtonModal from './LongButtonModal';

const CoBrowseIndicator = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const agentName = t('openReplay.agentNameDefault');
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  const [showSharingEndedModal, setShowSharingEndedModal] = useState(false);
  const [showAgentDisconnectedModal, setShowAgentDisconnectedModal] =
    useState(false);
  const [showDenyToast, setShowDenyToast] = useState(false);
  const isUserInitiatedStopRef = useRef(false);
  const wasSharingActiveRef = useRef(false);
  const denyToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    setCoBrowseCallbacks({
      onConsentNeeded: () => {
        setShowAgentDisconnectedModal(false);
        setShowConsentModal(true);
      },
      onAgentAllowed: () => {
        setShowAgentDisconnectedModal(false);
        setShowConsentModal(false);
        setIsVisible(true);
        wasSharingActiveRef.current = true;
      },
      onAgentDisconnect: () => {
        const hadActiveSharing = wasSharingActiveRef.current;
        setIsVisible(false);
        setShowStopModal(false);
        setShowConsentModal(false);
        wasSharingActiveRef.current = false;

      /* 
      ** When the agent disconnects by going back on co-browse session or refresh cobrowse session , 
       we show a modal to the user informing them that the agent has disconnected. **/
        if (!isUserInitiatedStopRef.current && hadActiveSharing) {
          setShowAgentDisconnectedModal(true);
          stopCoBrowsing(); 
        }

        isUserInitiatedStopRef.current = false;
      },
      onAgentDeclined: () => {
        setIsVisible(false);
        setShowConsentModal(false);
        wasSharingActiveRef.current = false;
      },
      onSessionEnded: () => {
        setIsVisible(false);
        setShowConsentModal(false);
        setShowStopModal(false);
        setShowSharingEndedModal(false);
        setShowAgentDisconnectedModal(false);
        wasSharingActiveRef.current = false;
        isUserInitiatedStopRef.current = false;
      },
    });

    return () => {
      clearCoBrowseCallbacks();
    };
  }, []);

  if (
    !isVisible &&
    !showConsentModal &&
    !showDenyToast &&
    !showSharingEndedModal &&
    !showAgentDisconnectedModal
  )
    return null;

  const handleConsentApprove = () => {
    approveVendorConsent();
    setShowConsentModal(false);
  };

  const handleConsentDecline = () => {
    denyVendorConsent();
    setShowConsentModal(false);
    setShowDenyToast(true);
    if (denyToastTimeoutRef.current) {
      clearTimeout(denyToastTimeoutRef.current);
    }
    denyToastTimeoutRef.current = setTimeout(() => {
      setShowDenyToast(false);
    }, 3000);
  };

  const handleStopSharing = () => {
    isUserInitiatedStopRef.current = true;
    wasSharingActiveRef.current = false;
    // stopCoBrowsing() dispatches AGENT_DISCONNECT do not dispatch
    // here first or the event fires twice, resetting isUserInitiatedStopRef
    // before handleAgentDisconnect can read it.
    stopCoBrowsing();
    setIsVisible(false);
    setShowStopModal(false);
    setShowAgentDisconnectedModal(false);
    setShowSharingEndedModal(true);
  };

  return (
    <>
      {isVisible && (
        <div
          className="cobrowse-indicator-banner"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1080,
          }}
        >
          <TimerBanner
            description={t('openReplay.indicator.bannerDescription')}
            onStop={() => setShowStopModal(true)}
            running
            stopLabel={t('openReplay.indicator.stopLabel')}
            title={t('openReplay.indicator.bannerTitle', { agentName })}
            variant="withAnimation"
            desktopCollapse={true}
            collapseDelay={5}
            desktopCollapseDelay={5}
          />
        </div>
      )}

      {/* When clicked on stop sharing banner we show this modal to stop sharing or keep sharing */}
      <LongButtonModal
        isOpen={showStopModal}
        header={t('openReplay.indicator.stopModalHeader')}
        body={t('openReplay.indicator.stopModalBody')}
        buttonLabel={t('openReplay.indicator.stopModalConfirm')}
        buttonTestId="stopSharingButton"
        onConfirm={handleStopSharing}
        secondaryButtonLabel={t('openReplay.indicator.stopModalCancel')}
        secondaryButtonTestId="keepSharingButton"
        onSecondaryClick={() => setShowStopModal(false)}
      />

      {/* Sharing ended modal shown when the user ends the session */}
      <LongButtonModal
        isOpen={showSharingEndedModal}
        header={t('openReplay.indicator.sharingEndedHeader')}
        body={t('openReplay.indicator.sharingEndedBody')}
        buttonLabel={t('openReplay.indicator.doneButton')}
        buttonTestId="sharingEndedDoneButton"
        onConfirm={() => setShowSharingEndedModal(false)}
      />
      {/* Agent disconnected modal shown when the agent disconnects */}
      <LongButtonModal
        isOpen={showAgentDisconnectedModal}
        header={t('openReplay.indicator.agentDisconnectedHeader')}
        body={t('openReplay.indicator.agentDisconnectedBody')}
        buttonLabel={t('openReplay.indicator.doneButton')}
        buttonTestId="agentDisconnectedDoneButton"
        onConfirm={() => setShowAgentDisconnectedModal(false)}
      />

      <CoBrowseConsentModal
        isOpen={showConsentModal}
        agentName={agentName}
        onApprove={handleConsentApprove}
        onDecline={handleConsentDecline}
      />

      {showDenyToast && (
        <output
          style={{
            position: 'fixed',
            top: '90px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
          }}
        >
          <GlobalDenyAlert dataTestId="cobrowse-deny-toast">
            {t('openReplay.consentModal.denyToast')}
          </GlobalDenyAlert>
        </output>
      )}
    </>
  );
};

export default CoBrowseIndicator;
